import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { CircularProgress } from '@material-ui/core'

import { createAddOnSetupIntent, setAddOnDefaultPaymentMethod } from '../../../store/actions/addOnRenewalActions'
import { connectedStripe } from './connectedStripe'

const cardOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            letterSpacing: '0.025em',
            fontFamily: 'Source Code Pro, monospace',
            '::placeholder': { color: '#aab7c4' }
        },
        invalid: { color: '#9e2146' }
    }
}

// Inside the connected-account <Elements>, so useStripe is that account's.
const CardSetupForm = ({ loginToken, renewalId, setup, onSaved }) => {
    const stripe = useStripe()
    const elements = useElements()
    const dispatch = useDispatch()
    const [saving, setSaving] = useState(false)
    const [errorMsg, setErrorMsg] = useState(null)

    const save = async (event) => {
        event.preventDefault()
        if (saving || !stripe || !elements) { return }
        setSaving(true)
        setErrorMsg(null)

        const result = await stripe.confirmCardSetup(setup.clientSecret, {
            payment_method: { card: elements.getElement(CardElement) }
        })
        if (result.error) {
            setErrorMsg(result.error.message || 'Your card could not be saved')
            setSaving(false)
            return
        }

        dispatch(setAddOnDefaultPaymentMethod(loginToken, renewalId, setup.setupIntentId, (err, data) => {
            setSaving(false)
            if (err) {
                setErrorMsg(err.message || 'Your card could not be saved')
                return
            }
            onSaved(data)
        }))
    }

    return <form onSubmit={save} noValidate autoComplete="off">
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <Paper style={{ padding: '14px 12px', border: '1px solid #e3e6eb', borderRadius: 10 }}><CardElement options={cardOptions} /></Paper>
            </Grid>
            {errorMsg && <Grid item><Typography style={{ color: '#a83232' }}>{errorMsg}</Typography></Grid>}
            <Grid item>
                <Button type="submit" fullWidth variant="contained" size="large" color="primary" disabled={saving || !stripe}>
                    {saving ? <span>Saving card <CircularProgress size={'14px'} /></span> : 'Save card and continue'}
                </Button>
            </Grid>
        </Grid>
    </form>
}

/**
 * Self-pay with no card on file: save a card to the add-on's own Stripe
 * customer (a SetupIntent on the connected account, confirmed with Stripe
 * Elements, then made the default) before going on.
 */
const CardSetupStep = ({ loginToken, renewalId, onSaved, notice }) => {
    const dispatch = useDispatch()
    const [setup, setSetup] = useState(null)
    const [errorMsg, setErrorMsg] = useState(null)
    const [attempt, setAttempt] = useState(0)

    useEffect(() => {
        setErrorMsg(null)
        dispatch(createAddOnSetupIntent(loginToken, renewalId, (err, data) => {
            if (err || !data || !data.clientSecret) {
                setErrorMsg((err && err.message) || 'Card entry is unavailable right now')
                return
            }
            setSetup(data)
        }))
    }, [attempt, dispatch, loginToken, renewalId])

    return <Grid container direction="column" spacing={2}>
        <Grid item>
            <Typography variant="h6">Add a card</Typography>
            <Typography color="textSecondary">
                {notice || 'Your plan will be billed monthly to this card. It is saved to your own account.'}
            </Typography>
        </Grid>
        <Grid item>
            {errorMsg ? <Fragment>
                <Typography style={{ color: '#a83232' }}>{errorMsg}</Typography>
                <Button variant="outlined" onClick={() => setAttempt(attempt + 1)}>Try again</Button>
            </Fragment> : !setup ? <span>Loading card entry <CircularProgress size={'14px'} /></span> :
                <Elements stripe={connectedStripe(setup.connectedAccountId)} key={setup.setupIntentId}>
                    <CardSetupForm loginToken={loginToken} renewalId={renewalId} setup={setup} onSaved={onSaved} />
                </Elements>}
        </Grid>
    </Grid>
}

export default CardSetupStep
